/** TicketsPage — Kanban K-B (Tabular Density). Pixel-perfect del mockup. */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger,
  DropdownMenuSubContent, DropdownMenuTrigger, DropdownMenuPortal,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Ticket as TicketIcon, Search,
  Mail, Phone, Globe, MessageCircle, User as UserIcon,
  RefreshCw, AlertCircle, X, Plus, Filter, UserPlus, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDroppable, useDraggable,
  DragOverlay, closestCenter,
} from '@dnd-kit/core';
import { useAuth } from '../../context/AuthContext';
import { tienePermiso } from '../../utils/navPermisos';
import { getSlaState } from './components/slaIndicator';
import TicketPresencialModal from './components/TicketPresencialModal';
import TicketManualModal from './components/TicketManualModal';
import './tickets-theme.css';

const ESTADOS = [
  { key: 'NUEVO',      label: 'Nuevo' },
  { key: 'ASIGNADO',   label: 'Asignado' },
  { key: 'EN_PROCESO', label: 'En proceso' },
  { key: 'TERMINADO',  label: 'Terminado' },
];

/** Mapa de transiciones permitidas (espejo del backend ticket_router.py). */
const TRANSICIONES_VALIDAS = {
  NUEVO:      new Set(['ASIGNADO', 'EN_PROCESO', 'TERMINADO']),
  ASIGNADO:   new Set(['EN_PROCESO', 'TERMINADO']),
  EN_PROCESO: new Set(['TERMINADO']),
  TERMINADO:  new Set(['EN_PROCESO']),
};

const ICONOS_CANAL = { Mail, Phone, Globe, MessageCircle, User: UserIcon };

/** Caché a nivel de módulo (stale-while-revalidate) para render instantáneo
 *  al navegar/volver del detalle. Se invalida al cambiar de empresa (tenant). */
const kanbanCache = { empresaId: null, tickets: null, responsables: null, prioridades: null, canales: null, updatedAt: null };

/** ¿Hay datos en caché para esta empresa? (SIN chequeo de tiempo). Se usa para
 *  el render inicial: mientras haya datos, NUNCA se vacía el tablero — solo se
 *  refresca de forma sutil, en vez de vaciar y recargar. */
const hasKanbanCacheData = (empresaId) =>
  kanbanCache.empresaId === empresaId &&
  Array.isArray(kanbanCache.tickets) &&
  kanbanCache.tickets.length > 0;

const initialsOf = (s) => {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};
const colorForString = (s) => {
  if (!s) return '#94a3b8';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h}, 45%, 55%)`;
};

/** Indicador sutil "Actualizado hace Xs" con dot pulsante. */
function RefreshIndicator({ lastUpdatedAt, refreshing, tick }) {
  if (!lastUpdatedAt) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000));
  let label;
  if (refreshing) label = 'Actualizando…';
  else if (diffSec < 5) label = 'Actualizado ahora';
  else if (diffSec < 60) label = `Actualizado hace ${diffSec}s`;
  else {
    const m = Math.floor(diffSec / 60);
    label = `Actualizado hace ${m} min`;
  }
  // tick unused but forces re-render; reference for lint
  void tick;
  return (
    <span className="tk-refresh-indicator" data-testid="tk-refresh-indicator" aria-live="polite">
      <span className={`tk-refresh-dot ${refreshing ? 'is-refreshing' : ''}`} />
      {label}
    </span>
  );
}

function TicketsSkeleton({ estadosVisibles, gridCols, isMobile }) {
  const cols = estadosVisibles && estadosVisibles.length ? estadosVisibles : ESTADOS;
  if (isMobile) {
    return (
      <div data-testid="tickets-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0' }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="tk-skel-card" style={{ height: 74, animationDelay: `${i * 0.08}s` }} />
        ))}
      </div>
    );
  }
  return (
    <div className="tk-kanban" data-testid="tickets-skeleton" style={{ gridTemplateColumns: gridCols, flex: 1 }}>
      {cols.map((est) => (
        <div key={est.key} className="tk-column">
          <div className="tk-col-header">
            <div className="tk-col-title-row">
              <span className="tk-col-title">{est.label} · <b>··</b></span>
            </div>
            <div className="tk-progress-bar"><div className="tk-progress-fill" style={{ width: '0%' }} /></div>
          </div>
          <div className="tk-col-body">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="tk-skel-card" style={{ animationDelay: `${i * 0.08}s` }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TicketsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { empresa, api, user } = useAuth();
  const puedeCrear = tienePermiso(user, 'ca_tickets', 'crear');
  const cacheHasData = hasKanbanCacheData(empresa?.id);
  // Los catálogos son casi estáticos y NO tienen TTL: se inicializan desde el caché de
  // módulo si existe para esta empresa (sincronizado con el guard `needCatalogs`), aunque
  // el caché de tickets esté vencido. Evita dejarlos vacíos y no recargarlos.
  const catalogsEnCache = kanbanCache.empresaId === empresa?.id && !!kanbanCache.responsables;
  // Persistencia de filtros del tablero por usuario (sessionStorage): se conservan al
  // entrar/salir del detalle o recargar la página, hasta que el usuario los limpie.
  const FILTROS_KEY = `cxplus_kanban_filtros_${user?.id || 'anon'}`;
  const _storedFiltros = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(FILTROS_KEY) || '{}') || {}; } catch { return {}; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FILTROS_KEY]);
  const [tickets, setTickets] = useState(() => (cacheHasData ? kanbanCache.tickets : []));
  const [responsables, setResponsables] = useState(() => (catalogsEnCache ? kanbanCache.responsables || [] : []));
  const [prioridades, setPrioridades] = useState(() => (catalogsEnCache ? kanbanCache.prioridades || [] : []));
  const [canales, setCanales] = useState(() => (catalogsEnCache ? kanbanCache.canales || [] : []));
  const [loading, setLoading] = useState(() => !cacheHasData);
  // Controla el skeleton de PRIMERA carga de forma robusta: se muestra hasta que
  // haya un primer resultado real (o caché). Desacoplado de `loading` para que
  // los guards del efecto no provoquen un flash de tablero vacío.
  const [loadedOnce, setLoadedOnce] = useState(() => cacheHasData);
  const [search, setSearch] = useState(() => _storedFiltros.search || '');
  const [filtroResp, setFiltroResp] = useState(() => _storedFiltros.filtroResp || 'all');
  const [filtroPri, setFiltroPri] = useState(() => _storedFiltros.filtroPri || 'all');
  const [filtroCanal, setFiltroCanal] = useState(() => _storedFiltros.filtroCanal || 'all');
  const [summary, setSummary] = useState(() => (cacheHasData ? kanbanCache.summary || null : null));
  const [termOffset, setTermOffset] = useState(0);
  const [termHasMore, setTermHasMore] = useState(false);
  const [termLoading, setTermLoading] = useState(false);
  const TERM_PAGE = 50;

  const filtroEstado = searchParams.get('estado');
  const filtroSla = searchParams.get('sla');
  const filtroVenc = searchParams.get('venc'); // 'por_vencer' | 'vencido'
  const limpiarFiltrosURL = () => setSearchParams({});

  // Al montar: si la URL no trae estado/sla/venc pero hay filtros guardados, restaurarlos.
  const _restoredRef = useRef(false);
  useEffect(() => {
    if (_restoredRef.current) return;
    _restoredRef.current = true;
    const hasUrl = searchParams.get('estado') || searchParams.get('sla') || searchParams.get('venc');
    const s = _storedFiltros;
    if (!hasUrl && (s.estado || s.sla || s.venc)) {
      const next = {};
      if (s.estado) next.estado = s.estado;
      if (s.sla) next.sla = s.sla;
      if (s.venc) next.venc = s.venc;
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guardar el set completo de filtros ante cualquier cambio (o borrar la key si todo
  // vuelve a su valor por defecto → así "Limpiar filtros" también los elimina del storage).
  useEffect(() => {
    const activos = {};
    if (filtroEstado) activos.estado = filtroEstado;
    if (filtroSla) activos.sla = filtroSla;
    if (filtroVenc) activos.venc = filtroVenc;
    if (search.trim()) activos.search = search.trim();
    if (filtroResp !== 'all') activos.filtroResp = filtroResp;
    if (filtroPri !== 'all') activos.filtroPri = filtroPri;
    if (filtroCanal !== 'all') activos.filtroCanal = filtroCanal;
    try {
      if (Object.keys(activos).length === 0) sessionStorage.removeItem(FILTROS_KEY);
      else sessionStorage.setItem(FILTROS_KEY, JSON.stringify(activos));
    } catch { /* noop */ }
  }, [FILTROS_KEY, filtroEstado, filtroSla, filtroVenc, search, filtroResp, filtroPri, filtroCanal]);

  // ============ Drag & Drop ============
  const [activeDrag, setActiveDrag] = useState(null);   // ticket que está siendo arrastrado
  const [assignModal, setAssignModal] = useState(null); // { ticket, destinoEstado, responsableId, prioridadId }
  const [kioskOpen, setKioskOpen] = useState(false);   // modal "Kiosko presencial"
  const [manualOpen, setManualOpen] = useState(false); // modal "Nuevo ticket" (manual/agente)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0); // re-render para "hace Xs"
  const activeDragRef = useRef(null);
  const assignModalRef = useRef(null);
  const fetchingRef = useRef(false);
  const abortRef = useRef(null);
  useEffect(() => { activeDragRef.current = activeDrag; }, [activeDrag]);
  useEffect(() => { assignModalRef.current = assignModal; }, [assignModal]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [isMobile, setIsMobile] = useState(() => {
    try { return typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches; } catch { return false; }
  });
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const patchTicket = async (ticketId, payload, prevTickets) => {
    try {
      const { data } = await api.patch(`/tickets/${ticketId}`, payload, {
        headers: { 'X-Tk-Source': 'kanban' },
      });
      setTickets((arr) => arr.map((t) => (t.id === ticketId ? { ...t, ...data } : t)));
      toast.success('Ticket actualizado');
      return true;
    } catch (e) {
      // Rollback optimistic
      if (prevTickets) setTickets(prevTickets);
      const detail = e?.response?.data?.detail || 'No se pudo actualizar el ticket';
      toast.error(typeof detail === 'string' ? detail : 'No se pudo actualizar');
      return false;
    }
  };

  const onDragStart = (event) => {
    const t = tickets.find((x) => x.id === event.active.id);
    setActiveDrag(t || null);
  };

  const onDragEnd = async (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const ticket = tickets.find((x) => x.id === active.id);
    const destino = over.id;
    if (!ticket || ticket.estado === destino) return;

    // Validar transición (espejo del backend)
    if (!TRANSICIONES_VALIDAS[ticket.estado]?.has(destino)) {
      toast.error(`Transición no permitida: ${ticket.estado} → ${destino}`);
      return;
    }

    // Pre-requisitos al salir de NUEVO
    if (ticket.estado === 'NUEVO' && (destino === 'ASIGNADO' || destino === 'EN_PROCESO')) {
      const faltaResp = !ticket.responsable_id;
      const faltaPri = !ticket.prioridad_id;
      if (faltaResp || faltaPri) {
        setAssignModal({
          ticket,
          destinoEstado: destino,
          responsableId: ticket.responsable_id || '',
          prioridadId: ticket.prioridad_id || '',
        });
        return;
      }
    }

    // Regla de negocio: el ticket SOLO puede terminarse enviando la respuesta final
    // ('Responder y Terminar'). Al arrastrar a "Terminado" no se cambia el estado:
    // se abre el detalle en la pestaña "Responder al cliente" para redactar el cierre.
    if (destino === 'TERMINADO') {
      navigate(`/admin/centro-ayuda/tickets/${ticket.id}?cerrar=1`);
      return;
    }

    // Optimistic update
    const prev = tickets;
    setTickets((arr) => arr.map((t) => (t.id === ticket.id ? { ...t, estado: destino } : t)));
    await patchTicket(ticket.id, { estado: destino }, prev);
  };

  const onAssignModalSave = async () => {
    if (!assignModal) return;
    const { ticket, destinoEstado, responsableId, prioridadId } = assignModal;
    if (!responsableId || !prioridadId) {
      toast.error('Debes seleccionar responsable y prioridad');
      return;
    }
    const prev = tickets;
    setTickets((arr) => arr.map((t) => (
      t.id === ticket.id ? { ...t, responsable_id: responsableId, prioridad_id: prioridadId, estado: destinoEstado } : t
    )));
    const ok = await patchTicket(ticket.id, {
      responsable_id: responsableId,
      prioridad_id: prioridadId,
      estado: destinoEstado,
    }, prev);
    if (ok) setAssignModal(null);
  };

  const buildFilterParams = () => {
    const p = {};
    if (filtroResp !== 'all' && filtroResp !== 'unassigned') p.responsable_id = filtroResp;
    if (filtroResp === 'unassigned') p.sin_asignar = true;
    if (filtroPri !== 'all') p.prioridad_id = filtroPri;
    if (filtroCanal !== 'all') p.canal_id = filtroCanal;
    if (search.trim()) p.search = search.trim();
    if (filtroVenc === 'por_vencer' || filtroVenc === 'vencido') p.venc = filtroVenc;
    return p;
  };

  // Params extra solo para la columna TERMINADO (SLA incumplido = sla_cumplido false).
  const termExtraParams = () => (filtroSla === 'incumplido' ? { sla_incumplido: true } : {});

  const fetchAll = async ({ silent = false } = {}) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (silent) setRefreshing(true);
    else setLoading(true);
    // Cancela cualquier carga previa en vuelo y crea un controlador para ésta.
    try { abortRef.current?.abort(); } catch { /* noop */ }
    const controller = new AbortController();
    abortRef.current = controller;
    const cfg = { timeout: 15000, signal: controller.signal };
    const fp = buildFilterParams();
    try {
      // Columnas activas: se traen COMPLETAS (paginado por lotes) para que el
      // agrupamiento, el % SLA y el drag-drop sean exactos a cualquier volumen.
      const activos = [];
      let off = 0;
      const PAGE = 500;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await api.get('/tickets', { ...cfg, params: { ...fp, estados: 'NUEVO,ASIGNADO,EN_PROCESO', limit: PAGE, offset: off } });
        const batch = data || [];
        activos.push(...batch);
        if (batch.length < PAGE) break;
        off += PAGE;
      }
      // Columna TERMINADO: diferida (primeras 50, "cargar más" de 50 en 50).
      const termRes = await api.get('/tickets', { ...cfg, params: { ...fp, ...termExtraParams(), estado: 'TERMINADO', limit: TERM_PAGE, offset: 0 } });
      const terminados = termRes.data || [];
      setTermOffset(terminados.length);
      setTermHasMore(terminados.length === TERM_PAGE);
      // Resumen (totales por estado + SLA de terminados), respeta los mismos filtros.
      const sumRes = await api.get('/tickets/board-summary', { ...cfg, params: fp });
      setSummary(sumRes.data || null);
      kanbanCache.summary = sumRes.data || null;

      const combined = [...activos, ...terminados];
      setTickets(combined);

      // Catálogos casi estáticos: una vez por sesión/empresa.
      const needCatalogs = !(kanbanCache.empresaId === empresa?.id && kanbanCache.responsables);
      if (needCatalogs) {
        const [rRes, pRes, cRes] = await Promise.all([
          api.get('/tickets/responsables', cfg),
          api.get('/tickets/prioridades', cfg),
          api.get('/tickets/canales', cfg),
        ]);
        setResponsables(rRes.data || []);
        setPrioridades(pRes.data || []);
        setCanales(cRes.data || []);
        kanbanCache.responsables = rRes.data || [];
        kanbanCache.prioridades = pRes.data || [];
        kanbanCache.canales = cRes.data || [];
      }
      kanbanCache.empresaId = empresa?.id;
      kanbanCache.tickets = combined;
      kanbanCache.updatedAt = Date.now();
      setLastUpdatedAt(Date.now());
      setLoadedOnce(true);
    } catch (e) {
      // Cancelaciones (navegación / pestaña en segundo plano): ignorar.
      const cancelado = e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
      if (cancelado) return;
      // Los timeouts/errores de red ya los notifica (deduplicado) el interceptor global.
      if (!silent) console.error('Error cargando tickets:', e);
      setLoadedOnce(true); // evita skeleton infinito ante error de red
    } finally {
      fetchingRef.current = false;
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  const loadMoreTerminados = async () => {
    if (termLoading || !termHasMore) return;
    setTermLoading(true);
    try {
      const fp = buildFilterParams();
      const { data } = await api.get('/tickets', { params: { ...fp, ...termExtraParams(), estado: 'TERMINADO', limit: TERM_PAGE, offset: termOffset } });
      const batch = data || [];
      setTickets((arr) => {
        const ids = new Set(arr.map((t) => t.id));
        return [...arr, ...batch.filter((t) => !ids.has(t.id))];
      });
      setTermOffset((o) => o + batch.length);
      setTermHasMore(batch.length === TERM_PAGE);
    } catch (e) {
      toast.error('No se pudo cargar más terminados.');
    } finally {
      setTermLoading(false);
    }
  };

  useEffect(() => {
    // Espera a que el tenant esté listo: mantiene el skeleton (loading) en vez de
    // vaciar el tablero prematuramente. Re-fetchea cuando la empresa cambia.
    if (!empresa?.id) return;
    if (!empresa?.modulo_tickets_activo) { setLoading(false); setLoadedOnce(true); return; }
    // Estándar: si hay datos en caché de esta empresa, se conservan en pantalla y
    // se refresca de forma SILENCIOSA (sin vaciar). Sin datos → carga bloqueante
    // con skeleton hasta que llegue el listado.
    const hayData = hasKanbanCacheData(empresa?.id);
    fetchAll({ silent: hayData });
    // eslint-disable-next-line
  }, [empresa?.id, empresa?.modulo_tickets_activo]);

  // Filtros server-side: al cambiar responsable/prioridad/canal/búsqueda/vencimiento
  // se re-consulta al backend (con debounce) para operar sobre TODOS los tickets.
  const filtersKey = `${filtroResp}|${filtroPri}|${filtroCanal}|${filtroVenc}|${filtroSla}|${search.trim()}`;
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (!empresa?.modulo_tickets_activo) return undefined;
    if (firstFilterRun.current) { firstFilterRun.current = false; return undefined; }
    const id = setTimeout(() => fetchAll({ silent: true }), 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [filtersKey]);

  // Mantener la caché sincronizada con actualizaciones locales (DnD optimista, etc.).
  useEffect(() => {
    if (kanbanCache.empresaId === empresa?.id) kanbanCache.tickets = tickets;
    // eslint-disable-next-line
  }, [tickets]);

  // Auto-refresh cada 30s mientras la pestaña esté activa.
  // Pausa cuando: pestaña oculta, drag activo, modal de asignación abierto, o carga inicial en curso.
  useEffect(() => {
    if (!empresa?.modulo_tickets_activo) return undefined;
    const POLL_MS = 30_000;
    let timer = null;
    const tryRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (activeDragRef.current) return;
      if (assignModalRef.current) return;
      fetchAll({ silent: true });
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tryRefresh, POLL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
        // Cancela cualquier carga en vuelo para que no expire en 2º plano (falso timeout).
        try { abortRef.current?.abort(); } catch { /* noop */ }
      }
    };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      try { abortRef.current?.abort(); } catch { /* noop */ }
    };
    // eslint-disable-next-line
  }, [empresa?.modulo_tickets_activo]);

  // Re-render del indicador "hace Xs" cada 5s
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  const filteredTickets = useMemo(() => {
    let arr = tickets;
    if (filtroResp !== 'all') {
      arr = filtroResp === 'unassigned'
        ? arr.filter((t) => !t.responsable_id)
        : arr.filter((t) => t.responsable_id === filtroResp);
    }
    if (filtroPri !== 'all') arr = arr.filter((t) => t.prioridad_id === filtroPri);
    if (filtroCanal !== 'all') arr = arr.filter((t) => t.canal_id === filtroCanal);
    if (filtroEstado === 'ABIERTO') arr = arr.filter((t) => t.estado !== 'TERMINADO');
    else if (filtroEstado) arr = arr.filter((t) => t.estado === filtroEstado);
    if (filtroSla === 'incumplido') arr = arr.filter((t) => t.sla_cumplido === false);
    if (filtroVenc === 'por_vencer' || filtroVenc === 'vencido') {
      arr = arr.filter((t) => t.venc_estado === filtroVenc);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((t) =>
        (t.codigo || '').toLowerCase().includes(q)
        || (t.cliente_email || '').toLowerCase().includes(q)
        || (t.asunto_original || '').toLowerCase().includes(q)
        || t.match_en_mensaje === true
      );
    }
    return arr;
  }, [tickets, search, filtroResp, filtroPri, filtroCanal, filtroEstado, filtroSla, filtroVenc]);

  const estadosVisibles = useMemo(() => {
    if (!filtroEstado) return ESTADOS;
    if (filtroEstado === 'ABIERTO') return ESTADOS.filter((e) => e.key !== 'TERMINADO');
    return ESTADOS.filter((e) => e.key === filtroEstado);
  }, [filtroEstado]);

  const grupos = useMemo(() => {
    const g = { NUEVO: [], ASIGNADO: [], EN_PROCESO: [], TERMINADO: [] };
    for (const t of filteredTickets) if (g[t.estado]) g[t.estado].push(t);
    return g;
  }, [filteredTickets]);

  const slaRatioPorEstado = useMemo(() => {
    const out = {};
    for (const key of Object.keys(grupos)) {
      const lista = grupos[key];
      const evaluados = lista.filter((t) => t.sla_cumplido !== null && t.sla_cumplido !== undefined);
      if (evaluados.length === 0) {
        const enRiesgo = lista.filter((t) => {
          const s = getSlaState(t);
          return s && (s.key === 'WARN' || s.key === 'VENCIDO');
        }).length;
        out[key] = {
          pct: lista.length > 0 ? Math.round(((lista.length - enRiesgo) / lista.length) * 100) : null,
          mode: 'riesgo',
          total: lista.length,
          enRiesgo,
        };
      } else {
        const ok = evaluados.filter((t) => t.sla_cumplido).length;
        out[key] = {
          pct: Math.round((ok / evaluados.length) * 100),
          mode: 'cumplido',
          total: lista.length,
          evaluados: evaluados.length,
          ok,
          fallidos: evaluados.length - ok,
        };
      }
    }
    // Totales por columna y SLA de TERMINADO desde el resumen del backend.
    // - 'sin asignar' ahora es server-side → summary refleja el filtro.
    // - 'SLA incumplido': activos = conteo en memoria (vencido por días hábiles, no
    //   representable en summary); TERMINADO = incumplidos (evaluados - ok).
    const slaInc = filtroSla === 'incumplido';
    if (summary?.counts) {
      for (const k of Object.keys(out)) {
        if (k === 'TERMINADO') continue;
        if (slaInc) out[k] = { ...out[k], total: grupos[k]?.length ?? 0 };
        else if (summary.counts[k] !== undefined) out[k] = { ...out[k], total: summary.counts[k] };
      }
      const ts = summary.terminado_sla;
      if (ts && ts.evaluados !== undefined) {
        out.TERMINADO = {
          mode: 'cumplido',
          total: slaInc ? (ts.evaluados - ts.ok) : (summary.counts.TERMINADO ?? out.TERMINADO?.total ?? 0),
          evaluados: ts.evaluados,
          ok: ts.ok,
          fallidos: ts.evaluados - ts.ok,
          pct: ts.pct,
        };
      }
    }
    return out;
  }, [grupos, summary, filtroSla, filtroResp]);

  if (!empresa?.modulo_tickets_activo) {
    return (
      <div className="tickets-scope tk-shell">
        <div style={{ background: 'var(--tk-surface)', border: '1px solid var(--tk-border)', borderRadius: 10, padding: 40, textAlign: 'center', maxWidth: 720, margin: '40px auto' }}>
          <AlertCircle style={{ width: 38, height: 38, color: 'var(--tk-amber)', margin: '0 auto 12px' }} />
          <p style={{ fontWeight: 500, color: 'var(--tk-text)' }}>El módulo Centro de Ayuda no está activado.</p>
        </div>
      </div>
    );
  }

  // Chips de filtros activos
  const chips = [];
  if (filtroResp !== 'all') {
    const label = filtroResp === 'unassigned' ? 'Sin asignar' : (responsables.find((r) => r.id === filtroResp)?.nombre || 'Responsable');
    chips.push({ key: 'resp', label: `Responsable: ${label}`, clear: () => setFiltroResp('all') });
  }
  if (filtroPri !== 'all') {
    const p = prioridades.find((x) => x.id === filtroPri);
    chips.push({ key: 'pri', label: `Prioridad: ${p?.nombre || filtroPri}`, color: p?.color, clear: () => setFiltroPri('all') });
  }
  if (filtroCanal !== 'all') {
    const c = canales.find((x) => x.id === filtroCanal);
    chips.push({ key: 'canal', label: `Canal: ${c?.nombre || filtroCanal}`, clear: () => setFiltroCanal('all') });
  }
  if (filtroEstado) chips.push({ key: 'estado', label: `Vista: ${filtroEstado === 'ABIERTO' ? 'Abiertos' : filtroEstado}`, clear: limpiarFiltrosURL, testid: 'btn-limpiar-kpi-filter' });
  if (filtroSla === 'incumplido') chips.push({ key: 'sla', label: 'Vista: SLA incumplido', clear: limpiarFiltrosURL });
  if (filtroVenc === 'por_vencer' || filtroVenc === 'vencido') {
    chips.push({
      key: 'venc',
      label: `Plazo legal: ${filtroVenc === 'vencido' ? 'Vencidos' : 'Por vencer'}`,
      color: filtroVenc === 'vencido' ? '#dc2626' : '#f59e0b',
      clear: () => { const n = Object.fromEntries(searchParams); delete n.venc; setSearchParams(n); },
      testid: 'chip-filtro-venc',
    });
  }

  const gridCols = estadosVisibles.length === 1 ? 'minmax(280px, 1fr)'
    : estadosVisibles.length === 2 ? 'repeat(2, minmax(280px, 1fr))'
    : estadosVisibles.length === 3 ? 'repeat(3, minmax(280px, 1fr))'
    : 'repeat(4, minmax(280px, 1fr))';

  return (
    <div className="tickets-scope tk-shell">
      {/* Header */}
      <div className="tk-view-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 6 }}>
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <TicketIcon style={{ width: 20, height: 20 }} />
            Tablero de Tickets
          </h1>
          <div className="tk-sub" style={{ marginTop: 2 }}>
            {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket visible' : 'tickets visibles'}
            {chips.length > 0 && ' · filtros aplicados'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <RefreshIndicator lastUpdatedAt={lastUpdatedAt} refreshing={refreshing} tick={tick} />
          <button className="tk-btn-toolbar" onClick={() => fetchAll()} disabled={loading} data-testid="btn-refresh-tickets">
            <RefreshCw style={{ width: 13, height: 13, marginRight: 5, display: 'inline', animation: (loading || refreshing) ? 'spin 1s linear infinite' : 'none' }} />
            Refrescar
          </button>
        </div>
      </div>

      {/* TOOLBAR plano (Mockup K-B) */}
      <div className="tk-toolbar-flat">
        <div className="tk-search">
          <Search style={{ width: 13, height: 13, color: 'var(--tk-text-soft)' }} />
          <input
            placeholder="Buscar por código, cliente o asunto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-tickets"
          />
        </div>

        {chips.length > 0 && (
          <>
            <span className="tk-toolbar-label-inline">Filtros activos:</span>
            {chips.map((c) => (
              <span key={c.key} className="tk-chip" data-testid={c.testid || `chip-filtro-${c.key}`}>
                {c.color && <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color }} />}
                {c.label}
                <span
                  className="tk-x"
                  onClick={c.clear}
                  role="button"
                  aria-label={`Quitar filtro ${c.key}`}
                  data-testid={`chip-filtro-${c.key}-remove`}
                >
                  <X style={{ width: 11, height: 11 }} />
                </span>
              </span>
            ))}
          </>
        )}

        {/* + Más filtros (Dropdown con submenús) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="tk-btn-ghost" data-testid="btn-mas-filtros" type="button">
              <Plus style={{ width: 12, height: 12 }} /> Más filtros
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Agregar filtro</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-responsable">Responsable</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-72 overflow-auto">
                  <DropdownMenuItem onSelect={() => setFiltroResp('unassigned')} data-testid="mf-resp-unassigned">Sin asignar</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {responsables.map((r) => (
                    <DropdownMenuItem key={r.id} onSelect={() => setFiltroResp(r.id)} data-testid={`mf-resp-${r.id}`}>
                      {r.nombre}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-prioridad">Prioridad</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {prioridades.map((p) => (
                    <DropdownMenuItem key={p.id} onSelect={() => setFiltroPri(p.id)} data-testid={`mf-pri-${p.id}`}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, marginRight: 8, display: 'inline-block' }} />
                      {p.nombre}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-canal">Canal</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {canales.map((c) => (
                    <DropdownMenuItem key={c.id} onSelect={() => setFiltroCanal(c.id)} data-testid={`mf-canal-${c.id}`}>
                      {c.nombre}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-sla">Estado SLA</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), sla: 'incumplido' })} data-testid="mf-sla-incumplido">
                    SLA incumplido
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), estado: 'ABIERTO' })} data-testid="mf-estado-abiertos">
                    Sólo abiertos
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), estado: 'TERMINADO' })} data-testid="mf-estado-terminados">
                    Sólo terminados
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-plazo-legal">Plazo legal</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), venc: 'por_vencer' })} data-testid="mf-venc-por-vencer">
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', marginRight: 8, display: 'inline-block' }} />
                    Por vencer
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), venc: 'vencido' })} data-testid="mf-venc-vencido">
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626', marginRight: 8, display: 'inline-block' }} />
                    Vencidos
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>

        <div style={{ flex: 1 }} />

        {/* CTAs de creación (solo con permiso Crear) */}
        {puedeCrear && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="tk-btn-cta"
            onClick={() => setManualOpen(true)}
            title="Registrar/regularizar un ticket manualmente"
            data-testid="btn-nuevo-ticket-manual"
            type="button"
          >
            <Plus style={{ width: 13, height: 13 }} /> Nuevo ticket
          </button>
          <button
            className="tk-btn-cta tk-btn-cta-outline"
            onClick={() => setKioskOpen(true)}
            title="Registrar visita presencial (kiosko)"
            data-testid="btn-kiosko-presencial"
            type="button"
          >
            <UserIcon style={{ width: 13, height: 13 }} /> Kiosko Presencial
          </button>
        </div>
        )}
      </div>

      {/* KANBAN / LISTA MÓVIL */}
      {loadedOnce && (refreshing || loading) && <div className="tk-refresh-bar" data-testid="tk-refresh-bar" />}
      {!loadedOnce ? (
        <TicketsSkeleton estadosVisibles={estadosVisibles} gridCols={gridCols} isMobile={isMobile} />
      ) : isMobile ? (
        <MobileTicketList
          estadosVisibles={estadosVisibles}
          grupos={grupos}
          slaRatioPorEstado={slaRatioPorEstado}
          responsables={responsables}
          prioridades={prioridades}
          canales={canales}
          onCardClick={(t) => navigate(`/admin/centro-ayuda/tickets/${t.id}`)}
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="tk-kanban" style={{ gridTemplateColumns: gridCols, flex: 1 }}>
            {estadosVisibles.map((est) => {
              const lista = grupos[est.key] || [];
              const slaInfo = slaRatioPorEstado[est.key];
              const pct = slaInfo?.pct;
              const isWarn = pct !== null && pct !== undefined && pct < 70;
              const accepts = activeDrag
                ? (est.key === activeDrag.estado || TRANSICIONES_VALIDAS[activeDrag.estado]?.has(est.key))
                : true;
              return (
                <KanbanColumn
                  key={est.key}
                  estado={est}
                  lista={lista}
                  pct={pct}
                  slaInfo={slaInfo}
                  isWarn={isWarn}
                  accepts={accepts}
                  totalCount={slaInfo?.total ?? lista.length}
                  onLoadMore={est.key === 'TERMINADO' ? loadMoreTerminados : undefined}
                  hasMore={est.key === 'TERMINADO' && termHasMore}
                  loadingMore={est.key === 'TERMINADO' && termLoading}
                  responsables={responsables}
                  prioridades={prioridades}
                  canales={canales}
                  onCardClick={(t) => navigate(`/admin/centro-ayuda/tickets/${t.id}`)}
                />
              );
            })}
          </div>

          <DragOverlay>
            {activeDrag ? (
              <div style={{ transform: 'rotate(2deg)', opacity: 0.92 }}>
                <TicketRow
                  ticket={activeDrag}
                  responsables={responsables}
                  prioridades={prioridades}
                  canales={canales}
                  onClick={() => {}}
                  isOverlay
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Modal: faltan datos para mover desde NUEVO */}
      <Dialog open={!!assignModal} onOpenChange={(o) => !o && setAssignModal(null)}>
        <DialogContent data-testid="dialog-asignar-pre-move">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-600" />
              Asignación requerida
            </DialogTitle>
            <DialogDescription>
              Para mover el ticket <strong>{assignModal?.ticket?.codigo}</strong> a{' '}
              <strong>{assignModal?.destinoEstado === 'ASIGNADO' ? 'Asignado' : 'En proceso'}</strong>{' '}
              necesitas asignar un responsable y una prioridad. Si cancelas, el ticket permanecerá en estado <strong>Nuevo</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-2">
            <div>
              <Label>Responsable</Label>
              <Select
                value={assignModal?.responsableId || ''}
                onValueChange={(v) => setAssignModal((m) => m && { ...m, responsableId: v })}
              >
                <SelectTrigger data-testid="select-modal-responsable">
                  <SelectValue placeholder="Seleccione un responsable" />
                </SelectTrigger>
                <SelectContent>
                  {responsables.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select
                value={assignModal?.prioridadId || ''}
                onValueChange={(v) => setAssignModal((m) => m && { ...m, prioridadId: v })}
              >
                <SelectTrigger data-testid="select-modal-prioridad">
                  <SelectValue placeholder="Seleccione una prioridad" />
                </SelectTrigger>
                <SelectContent>
                  {prioridades.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre} ({p.horas_sla}h)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal(null)} data-testid="btn-asignar-cancelar">
              Cancelar
            </Button>
            <Button
              onClick={onAssignModalSave}
              disabled={!assignModal?.responsableId || !assignModal?.prioridadId}
              data-testid="btn-asignar-guardar"
            >
              Asignar y mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal kiosk presencial */}
      <TicketPresencialModal
        open={kioskOpen}
        onClose={() => setKioskOpen(false)}
        onCreated={() => fetchAll({ silent: true })}
      />

      {/* Modal Nuevo ticket (manual/agente) */}
      <TicketManualModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        responsables={responsables}
        prioridades={prioridades}
        canales={canales}
        onCreated={() => fetchAll({ silent: true })}
      />
    </div>
  );
}

/** Vista móvil: lista/acordeón por estado (sin drag-and-drop; el estado se cambia
 *  entrando al detalle del ticket). */
function MobileTicketList({ estadosVisibles, grupos, slaRatioPorEstado, responsables, prioridades, canales, onCardClick }) {
  const [open, setOpen] = useState(() => {
    const def = { NUEVO: true, ASIGNADO: true, EN_PROCESO: true, TERMINADO: false };
    try {
      const saved = JSON.parse(localStorage.getItem('tk-mobile-secciones') || '{}');
      return { ...def, ...saved };
    } catch { return def; }
  });
  const toggleSec = (key, isOpen) => {
    setOpen((p) => {
      const next = { ...p, [key]: !isOpen };
      try { localStorage.setItem('tk-mobile-secciones', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  return (
    <div className="tk-mobile-list" data-testid="tk-mobile-list">
      {estadosVisibles.map((est) => {
        const lista = grupos[est.key] || [];
        const slaInfo = slaRatioPorEstado[est.key];
        const pct = slaInfo?.pct;
        const isWarn = pct !== null && pct !== undefined && pct < 70;
        const isOpen = open[est.key] !== false;
        return (
          <div className="tk-msec" key={est.key} data-testid={`msec-${est.key}`}>
            <button
              type="button"
              className="tk-msec-head"
              onClick={() => toggleSec(est.key, isOpen)}
              data-testid={`msec-toggle-${est.key}`}
              aria-expanded={isOpen}
            >
              <ChevronDown className={`tk-msec-chev ${isOpen ? 'open' : ''}`} />
              <span className="tk-msec-title">{est.label}</span>
              <span className="tk-msec-count" data-testid={`msec-count-${est.key}`}>{lista.length}</span>
              {pct !== null && pct !== undefined && (
                <span className="tk-msec-sla" style={{ color: isWarn ? 'var(--tk-amber)' : 'var(--tk-green)' }} data-testid={`msec-sla-${est.key}`}>
                  {pct}% SLA
                </span>
              )}
            </button>
            {isOpen && (
              <div className="tk-msec-body">
                {lista.length === 0 ? (
                  <div className="tk-msec-empty">Sin tickets</div>
                ) : (
                  lista.map((t) => (
                    <TicketRow
                      key={t.id}
                      ticket={t}
                      responsables={responsables}
                      prioridades={prioridades}
                      canales={canales}
                      onClick={() => onCardClick(t)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Columna del Kanban: zona droppable. */
function KanbanColumn({ estado, lista, pct, slaInfo, isWarn, accepts, totalCount, onLoadMore, hasMore, loadingMore, responsables, prioridades, canales, onCardClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: estado.key });
  const cls = ['tk-column', isOver && accepts ? 'is-over' : '', isOver && !accepts ? 'is-invalid' : ''].filter(Boolean).join(' ');
  const headerCount = totalCount !== undefined && totalCount !== null ? totalCount : lista.length;

  // Tooltip explicativo del % SLA según el modo (riesgo para estados activos,
  // cumplimiento histórico para TERMINADO).
  let slaTooltip;
  if (slaInfo && pct !== null && pct !== undefined) {
    if (slaInfo.mode === 'cumplido') {
      slaTooltip = slaInfo.evaluados === 0
        ? 'Sin tickets evaluados todavía.'
        : `Cumplimiento de SLA al cierre: ${slaInfo.ok} de ${slaInfo.evaluados} ticket(s) cerrados dentro del plazo (${pct}%). Fallidos: ${slaInfo.fallidos}.`;
    } else {
      const sanos = Math.max(0, slaInfo.total - slaInfo.enRiesgo);
      slaTooltip = slaInfo.total === 0
        ? 'Sin tickets en este estado.'
        : `Salud del SLA: ${sanos} de ${slaInfo.total} ticket(s) dentro de su tiempo objetivo (${pct}%). En riesgo o vencidos: ${slaInfo.enRiesgo}.`;
    }
  }

  return (
    <div ref={setNodeRef} className={cls} data-testid={`col-${estado.key}`}>
      <div className="tk-col-header">
        <div className="tk-col-title-row">
          <span className="tk-col-title">{estado.label} · <b>{String(headerCount).padStart(2, '0')}</b></span>
          {pct !== null && pct !== undefined && (
            <span
              className="tk-col-stats"
              style={{ color: isWarn ? 'var(--tk-amber)' : 'var(--tk-green)', cursor: slaTooltip ? 'help' : undefined }}
              title={slaTooltip}
              aria-label={slaTooltip}
              data-testid={`col-${estado.key}-sla`}
            >
              {pct}% SLA
            </span>
          )}
        </div>
        <div className={`tk-progress-bar ${isWarn ? 'tk-progress-warn' : ''}`}>
          <div className="tk-progress-fill" style={{ width: pct !== null && pct !== undefined ? `${pct}%` : '0%' }} />
        </div>
      </div>
      <div className="tk-col-body" style={{ maxHeight: 'calc(100vh - 280px)' }}>
        {lista.length === 0 ? (
          <div style={{ padding: '32px 8px', textAlign: 'center', fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>
            Sin tickets
          </div>
        ) : (
          lista.map((t) => (
            <DraggableTicketRow
              key={t.id}
              ticket={t}
              responsables={responsables}
              prioridades={prioridades}
              canales={canales}
              onClick={() => onCardClick(t)}
            />
          ))
        )}
        {onLoadMore && hasMore && (
          <button
            type="button"
            className="tk-loadmore-btn"
            onClick={onLoadMore}
            disabled={loadingMore}
            data-testid={`col-${estado.key}-load-more`}
            style={{ width: '100%', marginTop: 8, padding: '8px', fontSize: 11, fontWeight: 600, color: 'var(--tk-text-soft)', background: 'var(--tk-surface)', border: '1px dashed var(--tk-border)', borderRadius: 8, cursor: loadingMore ? 'default' : 'pointer' }}
          >
            {loadingMore ? 'Cargando…' : `Cargar más (${Math.max(0, (headerCount || 0) - lista.length)} restantes)`}
          </button>
        )}
      </div>
    </div>
  );
}

/** Wrapper Draggable que envuelve a TicketRow. */
function DraggableTicketRow({ ticket, onClick, ...rest }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: ticket.id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1, touchAction: 'none' }}
    >
      <TicketRow ticket={ticket} onClick={onClick} {...rest} />
    </div>
  );
}

function TicketRow({ ticket, responsables, prioridades, canales, onClick, isOverlay = false }) {
  const navigate = useNavigate();
  const resp = responsables.find((r) => r.id === ticket.responsable_id);
  const respNombre = resp?.nombre || ticket.responsable_nombre || null;
  const pri = prioridades.find((p) => p.id === ticket.prioridad_id);
  const canal = canales.find((c) => c.id === ticket.canal_id);
  const Icon = canal ? (ICONOS_CANAL[canal.icono] || Mail) : Mail;
  const sla = getSlaState(ticket);

  const dotClass =
    sla?.key === 'VENCIDO' ? 'tk-dot-late' :
    sla?.key === 'WARN' ? 'tk-dot-warn' :
    sla?.key === 'OK' || sla?.key === 'CUMPLIDO' ? 'tk-dot-ok' :
    sla?.key === 'INCUMPLIDO' ? 'tk-dot-late' : 'tk-dot-none';

  const slaPillClass =
    sla?.key === 'VENCIDO' || sla?.key === 'INCUMPLIDO' ? 'tk-sla-time-late' :
    sla?.key === 'WARN' ? 'tk-sla-time-warn' :
    sla?.key === 'OK' || sla?.key === 'CUMPLIDO' ? 'tk-sla-time-ok' : '';

  const slaText =
    sla?.key === 'CUMPLIDO' ? '✓' :
    sla?.key === 'INCUMPLIDO' ? '✗' :
    sla?.detalle?.replace(/restantes/i, '').replace(/vencido/i, '').trim() || sla?.label;

  return (
    <button onClick={onClick} className="tk-card" data-testid={isOverlay ? undefined : `ticket-card-${ticket.codigo}`} type="button">
      <div className="tk-card-line1">
        <span className="tk-code">{ticket.codigo}</span>
        <span className={`tk-dot-sla ${dotClass}`} title={sla?.label} />
        <span style={{ flex: 1 }} />
        {sla && (
          <span className={`tk-sla-time ${slaPillClass}`} data-testid={`card-sla-${ticket.codigo}`}>{slaText}</span>
        )}
      </div>
      <div className="tk-title-sm">{ticket.asunto_original || '(sin asunto)'}</div>
      {ticket.match_en_mensaje === true && (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            const q = ticket.match_mensaje_id ? `?msg=${ticket.match_mensaje_id}` : '';
            navigate(`/admin/centro-ayuda/tickets/${ticket.id}${q}`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation(); e.preventDefault();
              const q = ticket.match_mensaje_id ? `?msg=${ticket.match_mensaje_id}` : '';
              navigate(`/admin/centro-ayuda/tickets/${ticket.id}${q}`);
            }
          }}
          className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer transition-colors"
          data-testid={`card-match-mensaje-${ticket.codigo}`}
          title="Ver el correo anexado dentro del ticket"
        >
          <Mail className="w-3 h-3" /> Coincide en un correo del hilo
        </div>
      )}
      {(ticket.venc_estado === 'vencido' || ticket.venc_estado === 'por_vencer') && (
        <div style={{ marginTop: 4 }}>
          <span
            data-testid={`card-venc-${ticket.codigo}`}
            title={ticket.fecha_limite_legal ? `Plazo legal (días hábiles): ${new Date(ticket.fecha_limite_legal).toLocaleDateString('es-PE')}` : 'Plazo legal'}
            style={{
              fontSize: 9,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: ticket.venc_estado === 'vencido' ? '#fee2e2' : '#fef3c7',
              color: ticket.venc_estado === 'vencido' ? '#b91c1c' : '#b45309',
            }}
          >
            {ticket.venc_estado === 'vencido'
              ? 'VENCIDO'
              : `POR VENCER${typeof ticket.dias_habiles_restantes === 'number' ? ` · ${ticket.dias_habiles_restantes}d` : ''}`}
          </span>
        </div>
      )}
      <div className="tk-meta-line">
        <Icon style={{ color: canal?.color || 'var(--tk-text-muted)' }} />
        <span
          className="tk-meta-path"
          title={ticket.clasificacion_ruta || canal?.nombre || ''}
          data-testid={ticket.clasificacion_ruta ? `card-clasificacion-${ticket.codigo}` : undefined}
        >
          {ticket.clasificacion_ruta || canal?.nombre || 'Sin clasificar'}
        </span>
        {pri && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: `${pri.color}22`, color: pri.color, flexShrink: 0 }} title={`Prioridad: ${pri.nombre}`}>
            {pri.nombre.substring(0, 3).toUpperCase()}
          </span>
        )}
        <span className="tk-iniciales" style={{ background: respNombre ? colorForString(respNombre) : '#cbd5e1' }} title={respNombre || 'Sin asignar'}>
          {respNombre ? initialsOf(respNombre) : '—'}
        </span>
      </div>
    </button>
  );
}
